import { Injectable, computed, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { isHeyJivuDesktop, isHeyJivuMobileApp } from '../../constants/runtime.constants';

export type ClientRuntimeKind = 'browser' | 'desktop' | 'mobile';

export interface ClientRuntimeLimits {
  browserMaxSeconds: number;
  mobileMaxSeconds: number;
  desktopMaxSeconds: number;
}

export const DEFAULT_CLIENT_RUNTIME_LIMITS: ClientRuntimeLimits = {
  browserMaxSeconds: 10 * 60,
  mobileMaxSeconds: 15 * 60,
  desktopMaxSeconds: 60 * 60
};

@Injectable({
  providedIn: 'root'
})
export class ClientRuntimeLimitsService {
  private readonly http = inject(HttpClient);
  private readonly limitsSignal = signal<ClientRuntimeLimits | null>(null);
  private loadingPromise?: Promise<ClientRuntimeLimits>;

  readonly limits = computed(() => this.limitsSignal());

  runtimeKind(): ClientRuntimeKind {
    if (isHeyJivuDesktop()) return 'desktop';
    if (isHeyJivuMobileApp()) return 'mobile';
    return 'browser';
  }

  loadOnce(): Promise<ClientRuntimeLimits> {
    const current = this.limitsSignal();
    if (current) return Promise.resolve(current);

    this.loadingPromise ??= firstValueFrom(
      this.http.get<ClientRuntimeLimits>(`${environment.apiUrl}/settings/runtime-limits`)
    ).then(limits => {
      this.limitsSignal.set(limits);
      return limits;
    }).catch(() => {
      this.limitsSignal.set(DEFAULT_CLIENT_RUNTIME_LIMITS);
      return DEFAULT_CLIENT_RUNTIME_LIMITS;
    }).finally(() => {
      this.loadingPromise = undefined;
    });

    return this.loadingPromise;
  }

  maxSeconds(kind: ClientRuntimeKind = this.runtimeKind()): number | null {
    const limits = this.limitsSignal();
    if (!limits) return null;

    if (kind === 'desktop') return limits.desktopMaxSeconds;
    if (kind === 'mobile') return limits.mobileMaxSeconds;
    return limits.browserMaxSeconds;
  }
}
